import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { IoLogoFacebook } from "react-icons/io";
import {
  FiLogOut,
  FiUser,
  FiChevronDown,
  FiEye,
  FiEyeOff,
} from "react-icons/fi";
import { BiMailSend } from "react-icons/bi";
import { FaUsers } from "react-icons/fa";
import { FaTelegramPlane, FaWhatsapp } from "react-icons/fa";
import { toast } from "react-toastify";
import MAIN_LOGO from "../../assets/MAIN LOGO1.svg";
import { addResident, login, logout as logoutApi } from "../../Api/authApi";
import { getSocialMediaLinks } from "../../Api/socialMediaLinkApi";
import { IoMdLock } from "react-icons/io";
import { IoCloseOutline } from "react-icons/io5";
import { HiOutlineMenu } from "react-icons/hi"; // New import for Hamburger Icon
import {
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdOutlineLibraryBooks,
} from "react-icons/md";
import { RiLoginBoxLine, RiUserAddLine } from "react-icons/ri";
import { getEmergencyContacts } from "../../Api/emergencyContactsApi";
import { MdDarkMode, MdLightMode } from "react-icons/md";

// ------------------- Login Modal -------------------
export const LoginModal = ({ onClose, setUser, openRegisterModal }) => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    setFormErrors((prev) => {
      const newErrors = { ...prev };
      if (name === "email" && value.includes("@")) delete newErrors.email;
      if (name === "password" && value.length >= 6) delete newErrors.password;
      return newErrors;
    });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.email.trim()) errors.email = "Email is required.";
    else if (!formData.email.includes("@"))
      errors.email = "Please enter a valid email address.";

    if (!formData.password.trim()) errors.password = "Password is required.";
    else if (formData.password.length < 6)
      errors.password = "Password must be at least 6 characters.";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const res = await login(formData);
      const resident = res.data?.resident;

      if (!resident?.approval || resident.approval !== "approved") {
        toast.info(
          resident?.approval === "pending"
            ? "Your profile is checking, please wait for approval."
            : "Your account is not approved yet."
        );
        setLoading(false);
        return;
      }

      const userObj = {
        id: resident?.id || resident?.resident_id,
        name: resident?.full_name || "Resident",
        email: resident?.email || "",
        token: res.data.token,
        approval: resident.approval,
      };

      localStorage.setItem("user", JSON.stringify(userObj));
      setUser(userObj);
      toast.success("Login successful!");
      onClose();
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.error || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99]">
      <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md w-full max-w-md relative font-Poppins dark:bg-gray-800 dark:text-gray-200 transition-colors duration-300 dark:shadow-xl">
        <button
          onClick={onClose}
          className="absolute top-2 right-3 text-lg font-bold hover:text-red-500 dark:text-gray-300 dark:hover:text-red-400"
        >
          &times;
        </button>

        <h3 className="text-center text-2xl font-semibold mb-6">
          <span className="block text-gray-500 text-sm dark:text-gray-400">
            Welcome Back
          </span>
          <span className="bg-gradient-to-r from-Dark to-Light bg-clip-text text-transparent">
            Please Sign In
          </span>
        </h3>

        <form onSubmit={handleSubmit} className="space-y-6 relative">
          <div className="flex flex-col">
            <input
              type="text"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email"
              className={`w-full border p-3 rounded-md focus:outline-none focus:ring-1 focus:ring-Dark dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-gray-200 ${
                formErrors.email ? "border-red-500" : "border-gray-300"
              }`}
            />
            {formErrors.email && (
              <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
            )}
          </div>

          <div className="flex flex-col relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Password"
              className={`w-full border p-3 rounded-md pr-10 focus:outline-none focus:ring-1 focus:ring-Dark dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-gray-200 ${
                formErrors.password ? "border-red-500" : "border-gray-300"
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-Light hover:text-Dark dark:text-Light dark:hover:text-Dark"
            >
              {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
            </button>
            {formErrors.password && (
              <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-Dark hover:bg-opacity-90 text-white py-3 rounded-md transition-all dark:bg-Light dark:text-gray-900 dark:hover:bg-opacity-80 font-semibold"
          >
            {loading ? "Checking approval..." : "Sign In"}
          </button>
        </form>

        <div className="mt-4 text-center flex flex-col gap-2">
          <button
            type="button"
            onClick={() => navigate("/forgot-password")}
            className="text-sm text-Dark hover:text-Light dark:text-Light dark:hover:text-Dark"
          >
            Forgot Password?
          </button>

          <span className="text-gray-500 text-sm dark:text-gray-400">
            Don’t have an account?{" "}
            <button
              type="button"
              onClick={() => {
                onClose();
                openRegisterModal();
              }}
              className="text-sm font-semibold text-Dark hover:text-Light dark:text-Light dark:hover:text-Dark"
            >
              Sign Up
            </button>
          </span>
        </div>
      </div>
    </div>
  );
};

// ------------------- Register Modal -------------------
export const RegisterModal = ({ onClose, openLoginModal }) => {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    password: "",
    phone: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    setFormErrors((prev) => {
      const newErrors = { ...prev };
      if (name === "email" && value.includes("@")) delete newErrors.email;
      if (name === "password" && value.length >= 6) delete newErrors.password;
      if (name === "full_name" && value.trim()) delete newErrors.full_name;
      return newErrors;
    });
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/[^\d+]/g, "");
    if (!value.startsWith("+251"))
      value = "+251" + value.replace(/^(\+251)?/, "");
    if (value.length > 13) value = value.slice(0, 13);
    setFormData((prev) => ({ ...prev, phone: value }));

    setFormErrors((prev) => ({
      ...prev,
      phone: /^\+251[0-9]{9}$/.test(value)
        ? ""
        : "Phone must start with +251 and be 13 characters",
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.full_name.trim()) errors.full_name = "Full Name is required.";
    if (!formData.email.trim()) errors.email = "Email is required.";
    else if (!formData.email.includes("@"))
      errors.email = "Please enter a valid email address.";

    if (!formData.password.trim()) errors.password = "Password is required.";
    else if (formData.password.length < 6)
      errors.password = "Password must be at least 6 characters";

    if (!/^\+251[0-9]{9}$/.test(formData.phone))
      errors.phone = "Phone must start with +251 and be 13 characters.";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await addResident({
        full_name: formData.full_name.trim(),
        email: formData.email.trim(),
        password: formData.password,
        phone: formData.phone.trim(),
      });
      toast.success("Registration successful!");
      onClose();
    } catch (error) {
      let errMsg = "Registration failed. Please try again.";
      if (error.response?.data) {
        const data = error.response.data;
        if (data.errors) errMsg = Object.values(data.errors).join(" ");
        else if (data.error) errMsg = data.error;
        else if (data.message) errMsg = data.message;
      }
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99]">
      <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md w-full max-w-md relative font-Poppins dark:bg-gray-800 dark:text-gray-200 transition-colors duration-300 dark:shadow-xl">
        <button
          onClick={onClose}
          className="absolute top-2 right-3 text-lg font-bold hover:text-red-500 dark:text-gray-300 dark:hover:text-red-400"
        >
          &times;
        </button>

        <h3 className="text-center text-2xl font-semibold mb-6">
          <span className="block text-gray-500 text-sm dark:text-gray-400">
            Welcome to the Family
          </span>
          <span className="bg-gradient-to-r from-Dark to-Light bg-clip-text text-transparent">
            Join as a Member
          </span>
        </h3>

        <form onSubmit={handleSubmit} className="space-y-6 relative">
          <div className="flex flex-col">
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              placeholder="Full Name"
              className={`w-full border p-3 rounded-md focus:outline-none focus:ring-1 focus:ring-Dark dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-gray-200 ${
                formErrors.full_name ? "border-red-500" : "border-gray-300"
              }`}
            />
            {formErrors.full_name && (
              <p className="text-red-500 text-xs mt-1">
                {formErrors.full_name}
              </p>
            )}
          </div>

          <div className="flex flex-col">
            <input
              type="text"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email"
              className={`w-full border p-3 rounded-md focus:outline-none focus:ring-1 focus:ring-Dark dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-gray-200 ${
                formErrors.email ? "border-red-500" : "border-gray-300"
              }`}
            />
            {formErrors.email && (
              <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
            )}
          </div>

          <div className="flex flex-col relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Password"
              className={`w-full border p-3 rounded-md pr-10 focus:outline-none focus:ring-1 focus:ring-Dark dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-gray-200 ${
                formErrors.password ? "border-red-500" : "border-gray-300"
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-Light hover:text-Dark dark:text-Light dark:hover:text-Dark"
            >
              {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
            </button>
            {formErrors.password && (
              <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>
            )}
          </div>

          <div className="flex flex-col">
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handlePhoneChange}
              placeholder="Phone (+251)"
              className={`w-full border p-3 rounded-md focus:outline-none focus:ring-1 focus:ring-Dark dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-gray-200 ${
                formErrors.phone ? "border-red-500" : "border-gray-300"
              }`}
            />
            {formErrors.phone && (
              <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-Dark hover:bg-opacity-90 text-white py-3 rounded-md transition-all dark:bg-Light dark:text-gray-900 dark:hover:bg-opacity-80 font-semibold"
          >
            {loading ? "Registering..." : "Sign Up"}
          </button>
        </form>

        <div className="mt-4 text-center">
          <span className="text-gray-500 text-sm dark:text-gray-400">
            Already have an account?{" "}
            <button
              type="button"
              onClick={() => {
                onClose();
                openLoginModal();
              }}
              className="text-sm font-semibold text-Dark hover:text-Light underline dark:text-Light dark:hover:text-Dark"
            >
              Sign In
            </button>
          </span>
        </div>
      </div>
    </div>
  );
};

// ------------------- MenuBar Component -------------------
const MenuBar = ({ theme, toggleTheme }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [user, setUser] = useState(null);
  const [socialLinks, setSocialLinks] = useState({});
  const navigate = useNavigate();
  const userMenuRef = useRef(null);
  const [contacts, setContacts] = useState([]);
  const location = useLocation();
  const submenuTimers = useRef({});

  // Fetch emergency contacts: REPLACED MOCK WITH REAL API CALL
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const res = await getEmergencyContacts(); // Real API call
        setContacts(res.data || res || []); // fallback to empty array
      } catch {
        // Silent fail in production
        setContacts([]);
      }
    };

    fetchContacts();
  }, []);

  // Click outside for user/join menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        if (openSubMenu === "user" || openSubMenu === "join")
          setOpenSubMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openSubMenu]);

  // Load user and social links
  useEffect(() => {
    // Load user from localStorage
    try {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      if (storedUser?.name) setUser(storedUser);
    } catch {
      // Silent fail in production
      setUser(null);
    }

    // Fetch social links from API
    const fetchSocialLinks = async () => {
      try {
        const res = await getSocialMediaLinks(); // Real API call
        const links = {};

        (res.data || []).forEach((item) => {
          if (item.platform && item.url) {
            links[item.platform] = item.url;
          }
        });

        setSocialLinks(links);
      } catch {
        // Silent fail in production
        setSocialLinks({});
      }
    };

    fetchSocialLinks();
  }, []);

  // Logout handler: REPLACED MOCK WITH REAL API CALL
  const handleLogout = async () => {
    try {
      await logoutApi(); // Real API call
    } catch {
      // Silent fail: proceed with local logout regardless of API failure
    } finally {
      localStorage.removeItem("user");
      setUser(null);
    }
  };

  const navLinks = [
    { name: "Home", path: "/" },
    {
      name: "Community",
      path: "/community",
      children: [
        { name: "Our Residences", path: "/ccd-residences" },
        { name: "Community Updates", path: "/communications" },
        { name: "Governance & Updates", path: "/governance-updates" },
      ],
    },
    {
      name: "Service",
      path: "/service",
      children: [
        { name: "Facilities & Security", path: "/ccd-amenity" },
        { name: "Property Management", path: "/property-management" },
      ],
    },
    { name: "About Us", path: "/about-us" },
    {
      name: "Contact Us",
      path: "/contact-us",
      icon: <BiMailSend size={18} className="text-current" />,
    },
  ];

  const portalLinks = [
    {
      name: "Already a Member?",
      modal: "login",
      icon: <RiLoginBoxLine size={16} />,
    },
    {
      name: "Become a Member",
      modal: "register",
      icon: <RiUserAddLine size={16} />,
    },
  ];

  const openModal = (type) => {
    setModalType(type);
    setOpenSubMenu(null);
    setIsMenuOpen(false);
  };

  // Function to handle opening a submenu on mouse enter (for smooth desktop hover)
  const handleMouseEnter = (linkPath) => {
    clearTimeout(submenuTimers.current[linkPath]);
    setOpenSubMenu(linkPath);
  };

  // Function to handle closing a submenu on mouse leave (for smooth desktop hover)
  const handleMouseLeave = (linkPath) => {
    submenuTimers.current[linkPath] = setTimeout(() => {
      setOpenSubMenu(null);
    }, 200); // Small delay to allow moving to the submenu
  };

  return (
    // Z-INDEX CHANGE: MenuBar z-50 is fine for navbar
    <div className="sticky top-0 z-50 font-Poppins">
      {/* Top Info Bar */}
      {/* 💡 Applied dark mode classes for top bar background and text */}
      <div className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-0">
          {contacts.length > 0 && (
            <div className="hidden sm:flex w-auto text-left items-center gap-1">
              <FaWhatsapp size={16} className="text-green-600" />
              <a
                href={`tel:${contacts[0].phone}`}
                // 💡 Applied dark mode classes for link hover text
                className="hover:underline text-gray-700 dark:text-gray-300 hover:text-Light dark:hover:text-Light"
              >
                {contacts[0].phone}
              </a>
            </div>
          )}

          <div className="w-full sm:w-auto flex justify-center sm:justify-end space-x-4 sm:space-x-3">
            {/* Social Links (text color handled by parent and hover) */}
            {socialLinks.facebook && (
              <a
                href={socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-blue-700 transition-colors"
              >
                <IoLogoFacebook size={18} />
              </a>
            )}
            {socialLinks.telegram && (
              <a
                href={socialLinks.telegram}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-blue-500 transition-colors"
              >
                <FaTelegramPlane size={18} />
              </a>
            )}
            {socialLinks.whatsapp && (
              <a
                href={`https://wa.me/${socialLinks.whatsapp.replace(
                  /\D/g,
                  ""
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-green-600 transition-colors"
              >
                <FaWhatsapp size={18} />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Main Menu */}
      {/* 💡 Applied dark mode classes for main nav background and text */}
      <nav className="bg-white dark:bg-gray-900 shadow-md relative z-40 py-4">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center h-16 text-Dark dark:text-white">
          {/* Logo */}
          <NavLink to="/" className="flex items-center gap-2 hover:opacity-90">
            <img src={MAIN_LOGO} alt="CCD Logo" className="h-10 w-auto" />
            <span className="hidden sm:block text-lg font-semibold">
              CCD Gated Community
            </span>
          </NavLink>

          {/* Desktop Nav with Hover Animation */}
          <div className="hidden md:flex items-center space-x-6 font-medium flex-grow justify-center">
            {navLinks.map((link) => {
              const isChildActive =
                link.children &&
                link.children.some((child) => location.pathname === child.path);

              const isActiveLink =
                location.pathname === link.path || isChildActive;

              return (
                <div
                  key={link.path}
                  className="relative group"
                  onMouseEnter={() =>
                    link.children && handleMouseEnter(link.path)
                  }
                  onMouseLeave={() =>
                    link.children && handleMouseLeave(link.path)
                  }
                >
                  <NavLink
                    to={link.path}
                    // 💡 Applied dark mode classes for nav links
                    className={`flex items-center text-md px-2 py-2 transition-all duration-300 relative group-hover:scale-105 group-hover:text-Light ${
                      isActiveLink
                        ? "text-Light font-bold"
                        : "text-Dark dark:text-gray-200 hover:text-Light dark:hover:text-Light"
                    }`}
                  >
                    {/* Render icon if available */}
                    {link.icon && <span className="mr-2">{link.icon}</span>}
                    <span>{link.name}</span>

                    {/* Animated Underline */}
                    <span
                      className={`absolute bottom-0 left-0 h-[2px] w-0 bg-Light transition-all duration-300 ${
                        isActiveLink ? "w-full" : "group-hover:w-full"
                      }`}
                    ></span>

                    {/* Dropdown Arrow */}
                    {link.children && (
                      <FiChevronDown
                        className="ml-1 mt-1 transition-transform duration-300 group-hover:rotate-180"
                        size={14}
                      />
                    )}
                  </NavLink>

                  {link.children && (
                    // Animated Submenu Dropdown (scale and fade)
                    // 💡 Applied dark mode classes for submenu background and borders
                    <div
                      className={`absolute left-0 top-full mt-2 w-56 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-xl origin-top transition-all duration-200 ease-out z-50
                      ${
                        openSubMenu === link.path
                          ? "opacity-100 visible scale-y-100"
                          : "opacity-0 invisible scale-y-75"
                      }`}
                    >
                      {/* Submenu arrow pointer */}
                      {/* 💡 Applied dark mode classes for submenu arrow color and border */}
                      <div className="absolute -top-2 left-6 w-3 h-3 bg-white dark:bg-gray-800 rotate-45 border-l border-t border-gray-200 dark:border-gray-700"></div>
                      <div className="flex flex-col py-2">
                        {link.children.map((child) => (
                          <NavLink
                            key={child.path}
                            to={child.path}
                            onClick={() => setOpenSubMenu(null)}
                            // 💡 Applied dark mode classes for submenu links
                            className={({ isActive }) =>
                              `px-4 py-2 text-sm transition-all duration-150 transform hover:scale-[1.02] ${
                                isActive
                                  ? "bg-Light text-white font-semibold"
                                  : "text-Dark dark:text-gray-200 hover:bg-Light/10 dark:hover:bg-Light/20 hover:text-Light"
                              }`
                            }
                          >
                            {child.name}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Desktop User / Account */}
          <div
            className="hidden md:flex items-center relative space-x-3"
            ref={userMenuRef}
          >
            {user ? (
              // Logged-in user menu
              <div className="relative">
                <div className="flex items-center gap-3">
                  {/* 1. User Profile Button & Dropdown Menu */}
                  <div className="relative">
                    <button
                      onClick={() =>
                        setOpenSubMenu(openSubMenu === "user" ? null : "user")
                      }
                      // 💡 Applied dark mode classes for logged-in user button
                      // Note: Updated dark:bg-Light/10 to dark:bg-gray-700 for better visibility, and dark:hover to dark:hover:bg-gray-600
                      className="flex items-center p-2 pr-4 rounded-full bg-Dark/5 dark:bg-gray-700 text-Dark dark:text-white hover:bg-Dark/10 dark:hover:bg-gray-600 transition duration-200 focus:outline-none"
                    >
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-Dark dark:bg-Light text-white mr-2">
                        <FiUser size={18} />
                      </div>
                      <span className="text-sm font-semibold truncate max-w-[100px] md:max-w-none">
                        {user.name.split(" ")[0] || user.name}
                      </span>
                      <FiChevronDown
                        size={18}
                        className={`ml-1.5 transition-transform ${
                          openSubMenu === "user" ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {openSubMenu === "user" && (
                      // 💡 Applied dark mode classes for user submenu
                      // Dropdown container is dark:bg-gray-800 with dark:border-gray-700
                      <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded shadow-lg z-50 origin-top scale-y-100 opacity-100 transition-all duration-200 ease-out">
                        <NavLink
                          to="/my-profile"
                          onClick={() => setOpenSubMenu(null)}
                          // Dropdown links use dark:hover:bg-gray-700 for subtle hover effect
                          className="flex items-center gap-2 px-4 py-2 text-sm text-Dark dark:text-gray-200 hover:bg-Light/10 hover:text-Light dark:hover:bg-gray-700 dark:hover:text-Light transition-colors"
                        >
                          <FiUser size={16} /> My Profile
                        </NavLink>
                        <NavLink
                          to="/my-listings"
                          onClick={() => setOpenSubMenu(null)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-Dark dark:text-gray-200 hover:bg-Light/10 hover:text-Light dark:hover:bg-gray-700 dark:hover:text-Light transition-colors"
                        >
                          <MdOutlineLibraryBooks size={16} /> My Listings
                        </NavLink>
                        <button
                          onClick={() => {
                            handleLogout();
                            setOpenSubMenu(null);
                          }}
                          // Logout button uses standard red, but dark mode red text for contrast
                          className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm hover:bg-red-500 hover:text-white transition-colors text-red-600 dark:text-red-500"
                        >
                          <FiLogOut size={16} /> Logout
                        </button>
                      </div>
                    )}
                  </div>

                  {/* 2. Theme Toggle Button (Placed right of the menu) */}
                  <button
                    onClick={toggleTheme}
                    // 💡 Applied dark mode classes for theme toggle button
                    // Note: Updated dark:bg-Light/10 to dark:bg-gray-700 for subtle contrast
                    className="flex items-center gap-2 px-3 py-2 rounded-md transition bg-Light/10 text-Dark dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 hover:bg-Light/15"
                  >
                    {theme === "light" ? (
                      <MdDarkMode size={20} />
                    ) : (
                      <MdLightMode size={20} />
                    )}
                  </button>
                </div>
              </div>
            ) : (
              // Guest: HOA Members + Theme toggle
              <div className="relative flex items-center space-x-3">
                {/* HOA Members button */}
                <div className="relative">
                  <button
                    onClick={() =>
                      setOpenSubMenu(openSubMenu === "join" ? null : "join")
                    }
                    // 💡 Applied dark mode classes for guest button
                    className="flex items-center justify-center px-4 py-2 rounded-lg bg-Dark/15 dark:bg-Light/10 text-Dark dark:text-white hover:bg-Dark/20 dark:hover:bg-Light/20 border border-Dark/20 dark:border-Light/10 transition-all duration-300 font-semibold text-base"
                  >
                    <FaUsers size={18} className="mr-2" />
                    <span className="uppercase">Members</span>
                    <FiChevronDown
                      className={`transition-transform duration-300 ml-2 ${
                        openSubMenu === "join" ? "rotate-180" : ""
                      }`}
                      size={18}
                    />
                  </button>

                  {/* Members submenu */}
                  {openSubMenu === "join" && (
                    // 💡 Applied dark mode classes for join submenu
                    <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-md shadow-lg z-50 origin-top scale-y-100 opacity-100 transition-all duration-200 ease-out">
                      {/* Submenu arrow pointer */}
                      {/* 💡 Applied dark mode classes for submenu arrow color and border */}
                      <div className="absolute -top-2 right-6 w-3 h-3 bg-white dark:bg-gray-800 rotate-45 border-l border-t border-gray-200 dark:border-gray-700"></div>
                      <div className="flex flex-col py-2">
                        {portalLinks.map((item, idx) =>
                          item.modal ? (
                            <button
                              key={idx}
                              onClick={() => openModal(item.modal)}
                              // 💡 Applied dark mode classes for portal links
                              className="flex items-center gap-3 w-full text-left px-5 py-3 text-[15px] text-Dark dark:text-gray-200 hover:bg-Light/10 hover:text-Light dark:hover:bg-Light/20 dark:hover:text-Light whitespace-nowrap transition-colors"
                            >
                              {item.icon}
                              {item.name}
                            </button>
                          ) : (
                            <NavLink
                              key={idx}
                              to={item.path}
                              // 💡 Applied dark mode classes for portal links
                              className="flex items-center gap-3 px-5 py-3 text-[15px] text-Dark dark:text-gray-200 hover:bg-Light/10 hover:text-Light dark:hover:bg-Light/20 dark:hover:text-Light whitespace-nowrap transition-colors"
                            >
                              {item.icon}
                              {item.name}
                            </NavLink>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Theme toggle button */}
                <button
                  onClick={toggleTheme}
                  // 💡 Applied dark mode classes for theme toggle button
                  className="flex items-center gap-2 px-3 py-2 rounded-md transition bg-Light/10 text-Dark dark:bg-Light/10 dark:text-white dark:hover:bg-Light/20 hover:bg-Light/15"
                >
                  {theme === "light" ? (
                    <MdDarkMode size={20} />
                  ) : (
                    <MdLightMode size={20} />
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button - UPDATED FOR HAMBURGER/CLOSE ICON */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="focus:outline-none"
            >
              {isMenuOpen ? (
                // 💡 Applied dark mode classes for mobile menu icon
                <IoCloseOutline
                  size={28}
                  className="text-Dark dark:text-white hover:text-Light"
                /> // Close ('X') icon
              ) : (
                // 💡 Applied dark mode classes for mobile menu icon
                <HiOutlineMenu
                  size={28}
                  className="text-Dark dark:text-white hover:text-Light"
                /> // Hamburger icon
              )}
            </button>
          </div>
        </div>
        {/* Mobile Fullscreen Menu */}
        {isMenuOpen && (
          <div className="fixed inset-0 z-50">
            {/* Overlay */}
            <div
              className="absolute inset-0 bg-black bg-opacity-40 animate-fade-in"
              onClick={() => setIsMenuOpen(false)}
            />
            {/* Mobile Menu Content */}
            {/* 💡 Applied dark mode classes for mobile menu content and border */}
            <div className="absolute inset-0 bg-white dark:bg-gray-900 flex flex-col animate-slide-in">
              {/* Top bar */}
              {/* 💡 Applied dark mode classes for mobile menu top bar and border */}
              <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
                <NavLink
                  to="/"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-2"
                >
                  <img src={MAIN_LOGO} alt="CCD Logo" className="h-8 w-auto" />
                  <span className="font-semibold text-Dark dark:text-white text-normal">
                    CCD Gated Community
                  </span>
                </NavLink>
                <button onClick={() => setIsMenuOpen(false)}>
                  <IoCloseOutline
                    size={30}
                    className="text-Dark dark:text-white hover:text-Light"
                  />
                </button>
              </div>

              {/* Scrollable links */}
              <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
                {navLinks.map((link) => (
                  <div key={link.path}>
                    {link.children ? (
                      <>
                        <div className="flex items-center justify-between w-full">
                          <NavLink
                            to={link.path}
                            onClick={() => setIsMenuOpen(false)}
                            // 💡 Applied dark mode classes for mobile primary links with children
                            className={({ isActive }) =>
                              `flex-1 text-left text-lg font-medium py-3 rounded-md transition-colors ${
                                isActive
                                  ? "text-Light bg-gray-100 dark:bg-gray-800"
                                  : "text-Dark dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                              }`
                            }
                          >
                            {link.name}
                          </NavLink>
                          <button
                            onClick={() =>
                              setOpenSubMenu(
                                openSubMenu === link.path ? null : link.path
                              )
                            }
                            className="ml-2 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                          >
                            {/* 💡 Applied dark mode classes for mobile dropdown arrow */}
                            {openSubMenu === link.path ? (
                              <MdKeyboardArrowUp
                                size={20}
                                className="text-Dark dark:text-white"
                              />
                            ) : (
                              <MdKeyboardArrowDown
                                size={20}
                                className="text-Dark dark:text-white"
                              />
                            )}
                          </button>
                        </div>

                        {openSubMenu === link.path && (
                          <div className="ml-3 mt-2 space-y-2 border-l dark:border-gray-700 pl-3 transition-all duration-300 ease-in-out">
                            {link.children.map((child) => (
                              <NavLink
                                key={child.path}
                                to={child.path}
                                onClick={() => setIsMenuOpen(false)}
                                // 💡 Applied dark mode classes for mobile submenu links
                                className={({ isActive }) =>
                                  `block px-3 py-2 text-sm rounded-md transition-colors ${
                                    isActive
                                      ? "bg-Light text-white"
                                      : "text-gray-700 dark:text-gray-400 hover:bg-Light/10 hover:text-Light dark:hover:bg-Light/20 dark:hover:text-Light"
                                  }`
                                }
                              >
                                {child.name}
                              </NavLink>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <NavLink
                        to={link.path}
                        onClick={() => setIsMenuOpen(false)}
                        // 💡 Applied dark mode classes for mobile single links
                        className={({ isActive }) =>
                          `block text-lg font-medium py-3 rounded-md px-1 transition-colors ${
                            isActive
                              ? "text-Light border-b-2 border-Light"
                              : "text-Dark dark:text-gray-200 hover:text-Light hover:bg-gray-50 dark:hover:bg-gray-800/50"
                          }`
                        }
                      >
                        {link.name}
                      </NavLink>
                    )}
                  </div>
                ))}

                {/* Mobile User/Portal Links */}
                {/* 💡 Applied dark mode classes for mobile user/portal section border */}
                <div className="pt-6 border-t dark:border-gray-700 mt-6 space-y-4">
                  {user ? (
                    <>
                      <NavLink
                        to="/my-profile"
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center gap-3 text-lg font-medium py-3 px-1 rounded-md text-Dark dark:text-gray-200 hover:text-Light hover:bg-gray-50 dark:hover:bg-gray-800/50"
                      >
                        <FiUser size={20} /> My Profile
                      </NavLink>
                      <NavLink
                        to="/my-listings"
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center gap-3 text-lg font-medium py-3 px-1 rounded-md text-Dark dark:text-gray-200 hover:text-Light hover:bg-gray-50 dark:hover:bg-gray-800/50"
                      >
                        <MdOutlineLibraryBooks size={20} /> My Listings
                      </NavLink>
                      <button
                        onClick={() => {
                          handleLogout();
                          setIsMenuOpen(false);
                        }}
                        className="flex items-center gap-3 w-full text-left text-lg font-medium py-3 px-1 rounded-md text-red-600 dark:text-red-500 hover:text-white hover:bg-red-500 transition-colors"
                      >
                        <FiLogOut size={20} /> Logout
                      </button>
                    </>
                  ) : (
                    <>
                      {portalLinks.map((item, idx) => (
                        <button
                          key={idx}
                          onClick={() => openModal(item.modal)}
                          className="flex items-center gap-3 w-full text-left text-lg font-medium py-3 px-1 rounded-md text-Dark dark:text-gray-200 hover:text-Light hover:bg-gray-50 dark:hover:bg-gray-800/50"
                        >
                          {item.icon}
                          {item.name}
                        </button>
                      ))}
                    </>
                  )}
                  {/* 👇 New Light/Dark Mode Toggle Section for Mobile Menu 👇 */}
                  <div className="pt-4">
                    <button
                      onClick={() => {
                        toggleTheme();
                        // Optionally close the menu, but keeping it open might be better for user flow
                        // setIsMenuOpen(false);
                      }}
                      className="flex items-center gap-3 w-full text-left text-lg font-medium py-3 px-4 rounded-lg transition-colors bg-gray-100 dark:bg-gray-800 text-Dark dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      {theme === "light" ? (
                        <>
                          <MdDarkMode
                            size={22}
                            className="text-Dark dark:text-white"
                          />
                          Dark Mode
                        </>
                      ) : (
                        <>
                          <MdLightMode
                            size={22}
                            className="text-Dark dark:text-white"
                          />
                          Light Mode
                        </>
                      )}
                    </button>
                  </div>
                  {/* 👆 End of New Light/Dark Mode Toggle Section 👆 */}
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Modals - Already updated by my mock component for this example, but the actual components should be updated */}
      {modalType === "login" && (
        <LoginModal
          onClose={() => setModalType(null)}
          setUser={setUser}
          openRegisterModal={() => setModalType("register")}
        />
      )}
      {modalType === "register" && (
        <RegisterModal
          onClose={() => setModalType(null)}
          openLoginModal={() => setModalType("login")}
        />
      )}
    </div>
  );
};

export default MenuBar;
